/**
 * Circuit de validation des arrêtés (recette du 28/09, décisions d'Henrico).
 *  · VALIDEURS = comptes VPC (agents, managers, admins) : ils DÉCIDENT (valider, ou
 *    demander des corrections → retour en brouillon) ;
 *  · RELECTEURS = externes : ils donnent un AVIS (lien personnel ou e-mail).
 * Trois niveaux qui s'ajoutent : circuit PAR DÉFAUT (modeleId null, Réglages du ST),
 * circuit d'un MODÈLE sensible (modeleId), puis ajustement sur chaque arrêté.
 * Le serveur verrouille le rôle : un compte VPC est toujours valideur, un externe relecteur.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Mail, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Alert, Button } from '@/components/ui';

type Item = Awaited<ReturnType<typeof api.acteModeles.circuit>>[number];
type Rel = Awaited<ReturnType<typeof api.serviceTechnique.arretes.relecteurs.list>>['items'][number];
type Pers = Awaited<ReturnType<typeof api.workflows.annuaireDestinataires>>['users'][number];

export function CircuitValidationPanel({ modeleId = null }: { modeleId?: string | null }) {
  const [items, setItems] = useState<Item[]>([]);
  const [personnel, setPersonnel] = useState<Pers[]>([]);
  const [relecteurs, setRelecteurs] = useState<Rel[]>([]);
  const [valideurChoisi, setValideurChoisi] = useState('');
  const [relecteurChoisi, setRelecteurChoisi] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [c, a, r] = await Promise.all([
        api.acteModeles.circuit(modeleId),
        api.workflows.annuaireDestinataires().catch(() => ({ users: [], tiers: [] })),
        api.serviceTechnique.arretes.relecteurs.list().catch(() => ({ items: [] })),
      ]);
      setItems(c);
      setPersonnel(a.users.filter((u) => ['agent', 'manager', 'admin'].includes(u.role)));
      setRelecteurs(r.items);
    } catch (e) { setErreur(e instanceof Error ? e.message : 'Chargement impossible'); }
  }, [modeleId]);
  useEffect(() => { void charger(); }, [charger]);

  const agir = async (f: () => Promise<unknown>) => {
    setOccupe(true); setErreur(null);
    try { await f(); await charger(); } catch (e) { setErreur(e instanceof Error ? e.message : 'Action impossible'); }
    finally { setOccupe(false); }
  };
  const valideurs = items.filter((x) => x.role === 'valideur');
  const externes = items.filter((x) => x.role === 'relecteur');
  const personnelLibre = personnel.filter((u) => !valideurs.some((v) => v.userId === u.id));
  const relecteursLibres = relecteurs.filter((r) => !externes.some((x) => x.relecteurId === r.id));

  const ligne = (x: Item) => {
    const herite = !!modeleId && x.commun;
    return (
      <li key={x.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-bg/40 px-3 py-2 text-sm">
        <span className="min-w-0">
          <span className="font-medium text-ink">{x.nom}</span>
          {x.email && <span className="ml-2 text-xs text-ink-muted">{x.email}</span>}
          {herite && <span className="ml-2 rounded-r-pill bg-surface-hi px-2 py-0.5 text-2xs text-ink-muted">circuit par défaut</span>}
        </span>
        {!herite && (
          <Button type="button" variant="ghost" size="sm" disabled={occupe} aria-label={`Retirer ${x.nom} du circuit`}
            onClick={() => void agir(() => api.acteModeles.retirerDuCircuit(x.id))}>
            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
          </Button>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-4 rounded-r-xl border border-border bg-surface/55 p-4">
      <p className="text-sm text-ink-muted">
        {modeleId
          ? 'Ces personnes s’ajoutent au circuit par défaut pour les arrêtés créés depuis ce modèle.'
          : 'Appliqué automatiquement à chaque nouvel arrêté du service technique. On peut l’ajuster sur chaque arrêté avant l’envoi.'}
      </p>
      {erreur && <Alert variant="error">{erreur}</Alert>}

      <section aria-label="Valideurs">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink"><ShieldCheck className="h-4 w-4 text-tech-fg" aria-hidden="true" /> Valideurs — ils décident</h3>
        <p className="mb-2 text-xs text-ink-muted">Des comptes de l’application. Quand tous ont validé (et que les avis externes sont reçus), l’arrêté part seul à la signature ; une demande de corrections le renvoie en brouillon.</p>
        {valideurs.length ? <ul className="space-y-1.5">{valideurs.map(ligne)}</ul> : <p className="text-sm text-ink-muted">Aucun valideur : la validation reste manuelle (« Valider la relecture »).</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label htmlFor={`val-${modeleId ?? 'defaut'}`} className="sr-only">Choisir un valideur</label>
          <select id={`val-${modeleId ?? 'defaut'}`} value={valideurChoisi} onChange={(e) => setValideurChoisi(e.target.value)}
            className="h-10 min-w-[16rem] rounded-r-lg border border-border bg-surface/60 px-2.5 text-sm text-ink">
            <option value="">Choisir une personne…</option>
            {personnelLibre.map((u) => <option key={u.id} value={u.id}>{u.label}{u.service ? ` — ${u.service}` : ''}</option>)}
          </select>
          <Button type="button" size="sm" disabled={!valideurChoisi || occupe}
            onClick={() => void agir(async () => { await api.acteModeles.ajouterAuCircuit(modeleId, { role: 'valideur', userId: valideurChoisi }); setValideurChoisi(''); })}>
            Ajouter comme valideur
          </Button>
        </div>
      </section>

      <section aria-label="Relecteurs externes">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink"><Mail className="h-4 w-4 text-tech-fg" aria-hidden="true" /> Relecteurs externes — ils donnent un avis</h3>
        <p className="mb-2 text-xs text-ink-muted">Sans compte : ils reçoivent le PDF et un lien personnel, ou répondent par e-mail. L’arrêté attend leur avis avant la signature.</p>
        {externes.length ? <ul className="space-y-1.5">{externes.map(ligne)}</ul> : <p className="text-sm text-ink-muted">Aucun relecteur externe.</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label htmlFor={`rel-${modeleId ?? 'defaut'}`} className="sr-only">Choisir un relecteur externe</label>
          <select id={`rel-${modeleId ?? 'defaut'}`} value={relecteurChoisi} onChange={(e) => setRelecteurChoisi(e.target.value)}
            className="h-10 min-w-[16rem] rounded-r-lg border border-border bg-surface/60 px-2.5 text-sm text-ink">
            <option value="">{relecteursLibres.length ? 'Choisir un relecteur…' : 'Ajoutez d’abord des relecteurs externes (ci-dessus)'}</option>
            {relecteursLibres.map((r) => <option key={r.id} value={r.id}>{r.nom} — {r.email}</option>)}
          </select>
          <Button type="button" size="sm" disabled={!relecteurChoisi || occupe}
            onClick={() => void agir(async () => { await api.acteModeles.ajouterAuCircuit(modeleId, { role: 'relecteur', relecteurId: relecteurChoisi }); setRelecteurChoisi(''); })}>
            Ajouter comme relecteur
          </Button>
        </div>
      </section>
    </div>
  );
}
